import { useState, useEffect } from 'react'
import {
  ChevronRight, Users, Building2, Laptop, Wrench, CheckCircle2, TrendingUp, Heart, Award,
  GraduationCap, Stethoscope, Briefcase, Store, HeartHandshake, Rocket,
  Search, ShieldCheck, Globe, Quote, MessageCircle,
} from 'lucide-react'
import { SEO } from './SEO'
import { siteConfig } from '../config'
import { API_URL } from '../services/authService'
import { useSiteImages } from '../context/SiteImagesContext'
import { Reveal } from './Reveal'
import { Btn } from './Btn'
import { ImageWithFallback } from './ImageWithFallback'

interface HomePageProps {
  onNavigate: (page: string) => void
}

interface Temoignage {
  nom: string
  secteur: string
  texte: string
  photo?: string
}

interface ApiTestimonial {
  id: string
  nom: string
  poste: string
  entreprise: string
  texte: string
  photo?: string | null
}

export function HomePage({ onNavigate }: HomePageProps) {
  const images = useSiteImages()

  const waHref = `https://wa.me/${siteConfig.phoneUnformatted}?text=${encodeURIComponent(
    'Bonjour JE Pro Group, je souhaite obtenir des informations sur vos services. Voici mon besoin :'
  )}`
  const openWhatsApp = () => window.open(waHref, '_blank', 'noopener')

  // --- JSON-LD (Données Structurées pour l'Entreprise) ---
  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": siteConfig.companyName,
    "url": "https://www.jeprogroup.com/",
    "logo": "https://res.cloudinary.com/dz92jtvzt/image/upload/v1774278701/logo-no-bg_vbyfqb.png",
    "description": "Partenaire RH, administratif, digital et services aux entreprises au Cameroun.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": siteConfig.address,
      "addressCountry": "CM"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": siteConfig.phoneUnformatted,
      "contactType": "customer service",
      "email": siteConfig.email,
      "availableLanguage": ["French", "English"]
    },
    "sameAs": Object.values(siteConfig.socials)
  }

  // --- Données des 4 pôles d'activité ---
  const poles = [
    {
      icon: Users,
      title: 'RH & Recrutement',
      description: 'Recrutement, assistance RH, gestion administrative du personnel et accompagnement des PME dans leur organisation interne.',
      services: ['Recrutement de profils', 'Assistance RH PME', 'Gestion administrative du personnel'],
      cta: 'Découvrir les services RH',
      page: 'recrutement-entreprises',
    },
    {
      icon: Building2,
      title: 'Services aux entreprises',
      description: 'Solutions pratiques pour simplifier la gestion quotidienne des entreprises, commerces, cabinets, écoles et organisations.',
      services: ['Assistance administrative', 'Fournitures et matériel', 'Entretien et services généraux'],
      cta: 'Voir les services entreprises',
      page: 'services-aux-entreprises',
    },
    {
      icon: Laptop,
      title: 'Digital & IT',
      description: 'Accompagnement informatique et digital pour fiabiliser vos outils de travail et renforcer votre présence en ligne.',
      services: ['Support et maintenance informatique', 'Installation réseau', 'Sites web et applications'],
      cta: 'Découvrir Digital & IT',
      page: 'maintenance-informatique-pme',
    },
    {
      icon: Wrench,
      title: 'Prestations spécialisées',
      description: 'Appui opérationnel sur des besoins spécifiques : agriculture, mécanique industrielle, logistique et missions sur mesure.',
      services: ['Appui agricole', 'Mécanique industrielle', 'Missions opérationnelles'],
      cta: 'Demander une solution sur mesure',
      page: 'prestations-specialisees',
    },
  ]

  // --- Cibles ---
  const cibles = [
    { icon: Building2, label: 'PME', desc: 'Structurer les ressources humaines, l\'administration et les services internes.' },
    { icon: GraduationCap, label: 'Écoles & centres de formation', desc: 'Appui administratif, recrutement et services généraux.' },
    { icon: Stethoscope, label: 'Cliniques & cabinets médicaux', desc: 'Organisation, personnel et support opérationnel.' },
    { icon: Briefcase, label: 'Cabinets comptables, juridiques & conseils', desc: 'Assistance RH et administrative au quotidien.' },
    { icon: Store, label: 'Commerces structurés', desc: 'Recrutement, fournitures et services pratiques.' },
    { icon: HeartHandshake, label: 'ONG & organisations', desc: 'Gestion du personnel et appui aux missions.' },
    { icon: Rocket, label: 'Entrepreneurs & porteurs de projets', desc: 'Mise en place et accompagnement opérationnel.' },
  ]

  // --- Méthode ---
  const etapes = [
    { num: '1', title: 'Diagnostic du besoin', desc: 'Nous prenons le temps de comprendre votre situation et vos priorités.' },
    { num: '2', title: 'Proposition adaptée', desc: 'Une solution claire, adaptée à votre structure et à vos objectifs.' },
    { num: '3', title: 'Exécution de la mission', desc: 'Une réalisation professionnelle, avec des interlocuteurs disponibles.' },
    { num: '4', title: 'Suivi et amélioration', desc: 'Un suivi méthodique pour garantir un résultat durable.' },
  ]

  // --- Offres d'appel ---
  // diagnosticType correspond aux valeurs attendues par DiagnosticPage (pré-sélection du type)
  const offres = [
    {
      icon: Search,
      title: 'Diagnostic RH gratuit',
      text: 'Identifiez les points faibles de votre organisation RH, vos besoins de recrutement et les actions prioritaires à mettre en place.',
      cta: 'Demander un diagnostic RH',
      diagnosticType: 'Diagnostic RH',
    },
    {
      icon: ShieldCheck,
      title: 'Audit informatique express',
      text: 'Évaluez l\'état de vos postes, votre réseau local et vos besoins de support et maintenance informatique.',
      cta: 'Demander un audit IT',
      diagnosticType: 'Audit informatique',
    },
    {
      icon: Globe,
      title: 'Évaluation de votre site web',
      text: 'Analysez la clarté, la crédibilité et la capacité de conversion de votre site web actuel.',
      cta: 'Faire évaluer mon site',
      diagnosticType: 'Évaluation site web',
    },
  ]

  const goToDiagnostic = (diagnosticType: string) => {
    sessionStorage.setItem('diagnostic_preset_type', diagnosticType)
    onNavigate('diagnostic')
  }

  // --- Pourquoi nous choisir ---
  const atouts = [
    { icon: Award, title: 'Approche polyvalente', desc: 'Une seule structure pour vos besoins RH, administratifs, digitaux, informatiques et opérationnels.' },
    { icon: TrendingUp, title: 'Solutions adaptées aux PME', desc: 'Des offres pensées pour les réalités des entreprises camerounaises, simples à comprendre et à activer.' },
    { icon: Heart, title: 'Accompagnement de proximité', desc: 'Une équipe disponible, à l\'écoute et orientée vers des résultats concrets.' },
    { icon: CheckCircle2, title: 'Suivi professionnel', desc: 'Chaque mission est documentée, suivie et finalisée avec des livrables clairs.' },
  ]

  // --- Témoignages (fallback si l'API ne renvoie rien) ---
  const temoignagesFallback: Temoignage[] = [
    { nom: 'SEGEF SARL', secteur: 'Accompagnement entreprise', texte: 'JE Pro Group nous accompagne avec sérieux et professionnalisme dans nos besoins opérationnels.' },
    { nom: 'Directrice de PME, Douala', secteur: 'Recrutement', texte: 'Des profils pertinents présentés rapidement. Un vrai gain de temps pour notre recrutement.' },
    { nom: 'Responsable administratif', secteur: 'Assistance RH', texte: 'Notre gestion du personnel est enfin structurée. L\'équipe est réactive et à l\'écoute.' },
  ]
  const [temoignages, setTemoignages] = useState<Temoignage[]>(temoignagesFallback)

  useEffect(() => {
    let cancelled = false
    fetch(`${API_URL}/testimonials`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data: ApiTestimonial[]) => {
        if (cancelled || !Array.isArray(data) || data.length === 0) return
        setTemoignages(
          data.map((t) => ({
            nom: t.nom,
            secteur: [t.poste, t.entreprise].filter(Boolean).join(' · '),
            texte: t.texte,
            photo: t.photo || undefined,
          }))
        )
      })
      .catch(() => { /* API indisponible : on garde le fallback statique */ })
    return () => { cancelled = true }
  }, [])

  return (
    <div>
      <SEO
        title="JE Pro Group - Partenaire RH, administratif & services aux entreprises au Cameroun"
        description="JE Pro Group accompagne les PME et organisations au Cameroun : recrutement, gestion RH, assistance administrative, informatique, digital et services opérationnels."
        keywords="services aux entreprises Cameroun, recrutement entreprise Cameroun, assistance RH PME, maintenance informatique Douala, création site web PME"
        canonicalUrl="https://www.jeprogroup.com/"
        jsonLd={organizationJsonLd}
      />

      {/* Section 1 - Hero */}
      <section className="relative bg-gradient-to-br from-jepg-blue-light to-white py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-10 w-64 h-64 rounded-full blur-3xl" style={{ backgroundColor: '#0F70B7' }}></div>
          <div className="absolute bottom-10 left-10 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: '#EE7108' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal direction="left">
              <h1 className="text-4xl lg:text-5xl xl:text-6xl text-gray-900 mb-6">
                Votre partenaire RH, administratif et services aux entreprises au Cameroun
              </h1>
              <p className="text-lg lg:text-xl text-gray-600 mb-8">
                JE Pro Group accompagne les PME, organisations, écoles, cliniques, cabinets et commerces dans leurs besoins en recrutement, gestion RH, assistance administrative, informatique, digital et services opérationnels.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Btn variant="primary" size="lg" iconRight={<ChevronRight size={20} />} onClick={() => onNavigate('devis')}>
                  Demander un devis
                </Btn>
                <Btn variant="secondary" size="lg" icon={<MessageCircle size={20} />} onClick={openWhatsApp}>
                  Parler à un conseiller
                </Btn>
              </div>
            </Reveal>

            <Reveal direction="right" delay={0.15} className="relative">
              <ImageWithFallback
                src={images['home_hero']}
                alt="JE Pro Group - services aux entreprises au Cameroun"
                className="rounded-2xl shadow-2xl w-full h-64 sm:h-80 lg:h-[420px] object-cover"
                loading="eager"
                fetchpriority="high"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Section 2 - Les 4 pôles d'activité */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl text-gray-900 mb-4">Nos 4 pôles d'activité</h2>
            <p className="text-xl text-gray-600">Une réponse complète aux besoins de votre organisation</p>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {poles.map((pole, i) => {
              const Icon = pole.icon
              return (
                <Reveal key={pole.title} delay={i * 0.1} className="flex flex-col bg-white border border-gray-100 rounded-xl p-8 shadow-sm hover:shadow-xl transition-all">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="text-jepg-blue" size={28} />
                  </div>
                  <h3 className="text-xl text-gray-900 mb-3">{pole.title}</h3>
                  <p className="text-gray-600 mb-5">{pole.description}</p>
                  <ul className="space-y-2 mb-6">
                    {pole.services.map((s) => (
                      <li key={s} className="flex items-start gap-2 text-gray-700 text-sm">
                        <CheckCircle2 className="text-jepg-blue flex-shrink-0 mt-0.5" size={16} />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => onNavigate(pole.page)}
                    className="mt-auto text-jepg-blue hover:text-jepg-blue-hover flex items-center gap-1 font-medium"
                  >
                    {pole.cta} <ChevronRight size={16} />
                  </button>
                </Reveal>
              )
            })}
          </div>
        </div>
      </section>

      {/* Section 3 - Pour qui travaillons-nous ? */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12 max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl text-gray-900 mb-4">
              Nous accompagnons les structures qui veulent mieux s'organiser, recruter et se développer
            </h2>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {cibles.map((cible, i) => {
              const Icon = cible.icon
              return (
                <Reveal key={cible.label} delay={i * 0.05} className="flex gap-4 bg-white rounded-xl p-6 shadow-sm">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="text-jepg-blue" size={22} />
                  </div>
                  <div>
                    <h3 className="text-lg text-gray-900 mb-1">{cible.label}</h3>
                    <p className="text-gray-600 text-sm">{cible.desc}</p>
                  </div>
                </Reveal>
              )
            })}
          </div>
        </div>
      </section>

      {/* Section 4 - Comment ça marche ? */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12 max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl text-gray-900 mb-4">Comment ça marche ?</h2>
            <p className="text-lg text-gray-600">
              Nous commençons par comprendre votre besoin, puis nous vous proposons une solution claire, adaptée à votre structure et à vos priorités. Chaque mission est suivie avec méthode afin de garantir un résultat professionnel.
            </p>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {etapes.map((etape, i) => (
              <Reveal key={etape.num} delay={i * 0.1} className="text-center">
                <div className="w-16 h-16 rounded-full bg-jepg-blue text-white text-2xl font-semibold flex items-center justify-center mx-auto mb-4">
                  {etape.num}
                </div>
                <h3 className="text-lg text-gray-900 mb-2">{etape.title}</h3>
                <p className="text-gray-600 text-sm">{etape.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4bis - Annonces & offres */}
      <section className="py-14 bg-jepg-blue">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                <Briefcase className="text-white" size={26} />
              </div>
              <div>
                <h2 className="text-2xl lg:text-3xl text-white mb-1">Vous cherchez un emploi, une mission ou une formation ?</h2>
                <p className="text-blue-100">Consultez nos annonces d'offres d'emploi, de services et de formations, et postulez en ligne.</p>
              </div>
            </div>
            <Btn variant="outline" size="lg" iconRight={<ChevronRight size={18} />}
              className="border-white text-white hover:bg-white hover:text-jepg-blue flex-shrink-0"
              onClick={() => onNavigate('annonces')}
            >
              Voir les annonces
            </Btn>
          </Reveal>
        </div>
      </section>

      {/* Section 5 - Offres d'appel */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl text-gray-900 mb-4">Commencez par un diagnostic rapide</h2>
            <p className="text-xl text-gray-600">Une porte d'entrée simple pour évaluer vos besoins</p>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-8">
            {offres.map((offre, i) => {
              const Icon = offre.icon
              return (
                <Reveal key={offre.title} delay={i * 0.1} className="flex flex-col bg-white rounded-xl p-8 shadow-sm hover:shadow-xl transition-all">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: '#fef3e6' }}>
                    <Icon style={{ color: '#EE7108' }} size={28} />
                  </div>
                  <h3 className="text-xl text-gray-900 mb-3">{offre.title}</h3>
                  <p className="text-gray-600 mb-6 flex-grow">{offre.text}</p>
                  <Btn variant="outline" size="md" iconRight={<ChevronRight size={18} />} onClick={() => goToDiagnostic(offre.diagnosticType)} className="w-full">
                    {offre.cta}
                  </Btn>
                </Reveal>
              )
            })}
          </div>
        </div>
      </section>

      {/* Section 6 - Pourquoi choisir JE Pro Group */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl text-gray-900 mb-4">Pourquoi choisir JE Pro Group ?</h2>
            <p className="text-xl text-gray-600">Une approche qui allie polyvalence, proximité et professionnalisme</p>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {atouts.map((atout, i) => {
              const Icon = atout.icon
              return (
                <Reveal key={atout.title} delay={i * 0.1} className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-jepg-blue" size={30} />
                  </div>
                  <h3 className="text-lg text-gray-900 mb-3">{atout.title}</h3>
                  <p className="text-gray-600 text-sm">{atout.desc}</p>
                </Reveal>
              )
            })}
          </div>
        </div>
      </section>

      {/* Section 7 - Témoignages */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl text-gray-900 mb-4">Ils nous font confiance</h2>
            <p className="text-xl text-gray-600">Des organisations que nous accompagnons au quotidien</p>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-8">
            {temoignages.map((t, i) => (
              <Reveal key={t.nom} delay={i * 0.1} className="bg-white rounded-xl p-8 shadow-sm">
                <Quote className="text-blue-200 mb-4" size={32} />
                <p className="text-gray-700 mb-6 italic">"{t.texte}"</p>
                <div className="border-t border-gray-100 pt-4 flex items-center gap-3">
                  {t.photo && (
                    <img src={t.photo} alt={t.nom} className="w-11 h-11 rounded-full object-cover flex-shrink-0" loading="lazy" />
                  )}
                  <div>
                    <p className="text-gray-900 font-medium">{t.nom}</p>
                    <p className="text-sm text-gray-500">{t.secteur}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Section 8 - Appel à l'action final */}
      <section className="py-16 bg-gradient-to-br from-jepg-blue to-blue-800 text-white">
        <Reveal className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl mb-6">
            Vous avez un besoin en recrutement, RH, informatique ou services aux entreprises ?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Expliquez-nous votre besoin et recevez une orientation claire sur la solution la plus adaptée.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Btn variant="outline" size="lg" iconRight={<ChevronRight size={20} />}
              className="border-white text-white hover:bg-white hover:text-jepg-blue"
              onClick={() => onNavigate('devis')}
            >
              Demander un devis
            </Btn>
            <Btn variant="outline" size="lg" icon={<MessageCircle size={20} />}
              className="border-white text-white hover:bg-white hover:text-jepg-blue"
              onClick={openWhatsApp}
            >
              Écrire sur WhatsApp
            </Btn>
          </div>
        </Reveal>
      </section>
    </div>
  )
}
